import axios from 'axios';
import { getAuthToken } from '../utils/sessionAuth.js';

const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/api/settings`;

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getSettings = async () => {
  const response = await axios.get(API_URL, { headers: authHeaders() });
  return response.data;
};

const previewApns = async ({ certificatePem, privateKeyPem }) => {
  const response = await axios.post(
    `${API_URL}/apns/preview`,
    { certificatePem, privateKeyPem },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const renewApns = async ({ certificatePem, privateKeyPem }) => {
  const response = await axios.put(
    `${API_URL}/apns`,
    { certificatePem, privateKeyPem },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const saveApnsVendor = async ({
  certificatePem,
  certificateBase64,
  privateKeyPem,
  privateKeyPassword,
}) => {
  const response = await axios.put(
    `${API_URL}/apns/vendor`,
    { certificatePem, certificateBase64, privateKeyPem, privateKeyPassword },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const downloadApnsVendorCsr = async ({ email, country, commonName } = {}) => {
  const response = await axios.post(
    `${API_URL}/apns/vendor-csr`,
    { email, country, commonName },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const downloadVendorSignedCsr = async ({ email, country, commonName } = {}) => {
  const response = await axios.post(
    `${API_URL}/apns/vendor-signed-csr`,
    { email, country, commonName },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const downloadDepPublicCert = async ({ cn, validityDays } = {}) => {
  const params = {};
  if (cn) params.cn = cn;
  if (validityDays) params.validity_days = validityDays;
  const response = await axios.get(`${API_URL}/dep/public-cert`, {
    headers: authHeaders(),
    params,
    responseType: 'blob',
  });
  return response;
};

const renewDepToken = async ({ p7mBase64, force }) => {
  const response = await axios.put(
    `${API_URL}/dep/token`,
    { p7mBase64, force: Boolean(force) },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const renewVppToken = async ({ tokenText }) => {
  const response = await axios.put(
    `${API_URL}/vpp/token`,
    { tokenText },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const previewProfileSigning = async ({ certificatePem, privateKeyPem }) => {
  const response = await axios.post(
    `${API_URL}/profile-signing/preview`,
    { certificatePem, privateKeyPem },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const saveProfileSigning = async ({ certificatePem, privateKeyPem, enabled }) => {
  const response = await axios.put(
    `${API_URL}/profile-signing`,
    { certificatePem, privateKeyPem, enabled },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const generateProfileSigning = async ({ commonName, organization, validityDays } = {}) => {
  const response = await axios.post(
    `${API_URL}/profile-signing/generate`,
    { commonName, organization, validityDays },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const downloadProfileSigningPublicCert = async () => {
  const response = await axios.get(`${API_URL}/profile-signing/public-cert`, {
    headers: authHeaders(),
    responseType: 'blob',
  });
  return response;
};

const createSigningTrustProfile = async () => {
  const response = await axios.post(
    `${API_URL}/profile-signing/trust-profile`,
    {},
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const generateSupervisionIdentity = async ({ commonName, organization, validityDays } = {}) => {
  const response = await axios.post(
    `${API_URL}/supervision-identity/generate`,
    { commonName, organization, validityDays },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const downloadSupervisionIdentityP12 = async ({ password }) => {
  const response = await axios.post(
    `${API_URL}/supervision-identity/p12`,
    { password },
    {
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      responseType: 'blob',
    }
  );
  return response;
};

const saveConfiguratorOrganization = async (payload) => {
  const response = await axios.put(
    `${API_URL}/configurator-organization`,
    payload,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

const downloadConfiguratorOrganization = async ({ password }) => {
  const response = await axios.post(
    `${API_URL}/configurator-organization/export`,
    { password },
    {
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      responseType: 'blob',
    }
  );
  return response;
};

const organizationSettingsService = {
  getSettings,
  previewApns,
  renewApns,
  saveApnsVendor,
  downloadApnsVendorCsr,
  downloadVendorSignedCsr,
  downloadDepPublicCert,
  renewDepToken,
  renewVppToken,
  previewProfileSigning,
  saveProfileSigning,
  generateProfileSigning,
  downloadProfileSigningPublicCert,
  createSigningTrustProfile,
  generateSupervisionIdentity,
  downloadSupervisionIdentityP12,
  saveConfiguratorOrganization,
  downloadConfiguratorOrganization,
};
export default organizationSettingsService;
